import { gql, NetworkStatus, useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { format, subWeeks } from 'date-fns';
import type {
    NotificationsQuery,
    NotificationsQueryVariables,
    NotificationTypeFieldsFragment,
} from '@/graphql/types';
import { NotificationLevel } from '@/graphql/types';
import { identity } from 'lodash';
import { parseAndNormalizeDateRange } from '../../../utils/date-range';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { useUpdateSearchParams } from '../../../hooks/useUpdateSearchParams';
import { useDateRangeFilter } from '../../../hooks/useDateRangeFilter';
import { createAfterCursor } from '../../../utils/relay';
import { LOCAL_DATE_TIME_WITH_MILLISECONDS_FORMAT } from '../../../constants';
import {
    useResetPageOnFilterChange,
    type UseOrderAndPaginationControlReturn,
} from '../../table/hooks';

type LevelsFilter = Record<string, boolean>;
type NotificationNode = NotificationTypeFieldsFragment;

interface FiltersState {
    levels: LevelsFilter;
    searchInput: string;
    search: string;
    startDate: string;
    endDate: string;
    mpPk: string;
}

interface FiltrationControl {
    filters: FiltersState;
    setLevelsFilter: (key: string, value: boolean) => void;
    setDateRangeFilter: (startDate: string, endDate: string) => void;
    setMeasuringPointFilter: (value: string) => void;
    setSearchFilter: (value: string) => void;
}

interface NotificationEdge {
    node: NotificationNode | null;
}

const NOTIFICATIONS_FRAGMENT = gql`
    fragment NotificationTypeFields on NotificationType {
        id
        swarmType
        createdAt
        level
        data
        isLong
        isLegacy
    }
`;

const NOTIFICATIONS = gql`
    query notifications(
        $orderBy: String
        $before: String
        $after: String
        $first: Int
        $last: Int
        $levels: [NotificationLevel]
        $search: String
        $createdAt_Gte: DateTime
        $createdAt_Lte: DateTime
        $mpPk: Int
    ) {
        notifications(
            orderBy: $orderBy
            before: $before
            after: $after
            first: $first
            last: $last
            levels: $levels
            search: $search
            createdAt_Gte: $createdAt_Gte
            createdAt_Lte: $createdAt_Lte
            mpPk: $mpPk
        ) {
            totalCount
            edges {
                node {
                    ...NotificationTypeFields
                }
            }
            pageInfo {
                startCursor
                endCursor
            }
        }
    }
    ${NOTIFICATIONS_FRAGMENT}
`;

function getKeysWithTrueValues(obj: LevelsFilter): string[] {
    return Object.entries(obj)
        .filter(([, value]) => value)
        .map(([key]) => key);
}

function getLastWeekDateTimeLocal(): string {
    const now = new Date();
    const lastWeek = subWeeks(now, 1);
    return format(lastWeek, LOCAL_DATE_TIME_WITH_MILLISECONDS_FORMAT);
}

function createInitialFilters(searchParams: URLSearchParams): FiltersState {
    const initialSearch = searchParams.get('search') ?? '';
    const initialFilters: FiltersState = {
        levels: {},
        searchInput: initialSearch,
        search: initialSearch,
        startDate: searchParams.get('startDate') ?? getLastWeekDateTimeLocal(),
        endDate: searchParams.get('endDate') ?? '',
        mpPk: searchParams.get('mpPk') ?? '',
    };

    const levelsParam = searchParams.get('levels');
    if (levelsParam) {
        levelsParam.split(',').forEach((level) => {
            initialFilters.levels[level] = true;
        });
    } else {
        Object.values(NotificationLevel).forEach((level) => {
            initialFilters.levels[level] = true;
        });
    }

    return initialFilters;
}

export function useFiltrationControl(): FiltrationControl {
    const { searchParams, updateSearchParams } = useUpdateSearchParams();

    const [filters, setFilters] = useState<FiltersState>(() => createInitialFilters(searchParams));
    const debouncedSearch = useDebouncedValue(filters.searchInput);

    useEffect(() => {
        updateSearchParams('levels', getKeysWithTrueValues(filters.levels));
    }, [filters.levels, updateSearchParams]);

    useEffect(() => {
        if (filters.search === debouncedSearch) {
            return;
        }

        setFilters((prevState) => ({
            ...prevState,
            search: debouncedSearch,
        }));
        updateSearchParams('search', debouncedSearch);
    }, [debouncedSearch, filters.search, updateSearchParams]);

    // Handles changes to filters, updating both local state and URL search parameters.
    // Special handling for 'levels' is done in the 'useEffect' hook.
    const handleFilterChange = useCallback(
        (key: string, value: string | boolean, filterKey: 'levels' | null = null) => {
            setFilters((prevState) => {
                if (filterKey === 'levels') {
                    if (prevState[filterKey][key] === value) {
                        return prevState;
                    }

                    return {
                        ...prevState,
                        [filterKey]: {
                            ...prevState[filterKey],
                            [key]: value as boolean,
                        },
                    };
                }

                const valueKey = key as Exclude<keyof FiltersState, 'levels'>;
                if (prevState[valueKey] === value) {
                    return prevState;
                }

                return {
                    ...prevState,
                    [valueKey]: value as FiltersState[Exclude<keyof FiltersState, 'levels'>],
                };
            });
            if (filterKey !== 'levels' && key !== 'searchInput') {
                updateSearchParams(key, value);
            }
        },
        [updateSearchParams]
    );

    const setDateRangeFilter = useDateRangeFilter(setFilters);

    return {
        filters,
        setLevelsFilter(key: string, value: boolean) {
            return handleFilterChange(key, value, 'levels');
        },
        setDateRangeFilter,
        setMeasuringPointFilter(value: string) {
            return handleFilterChange('mpPk', value);
        },
        setSearchFilter(value: string) {
            return handleFilterChange('searchInput', value);
        },
    };
}

function getCommittedFilterSignature({
    levels,
    search,
    startDate,
    endDate,
    mpPk,
}: Omit<FiltersState, 'searchInput'>): string {
    return JSON.stringify({
        search,
        startDate,
        endDate,
        mpPk,
        activeLevels: getKeysWithTrueValues(levels).sort(),
    });
}

function createQueryVariables({
    currentPage,
    pageSize,
    orderBy,
    levels,
    search,
    startDate,
    endDate,
    mpPk,
}: {
    currentPage: number;
    pageSize: number;
    orderBy: string | null;
    levels: LevelsFilter;
    search: string;
    startDate: string;
    endDate: string;
    mpPk: string;
}) {
    function addIf<T, U>(value: T, key: string, transform: (value: T) => U = identity): object {
        return value ? { [key]: transform(value) } : {};
    }

    const dateRange = parseAndNormalizeDateRange({ startDate, endDate });
    const startDateIso = dateRange.startDate?.toISOString() ?? null;
    const endDateIso = dateRange.endDate?.toISOString() ?? null;

    return {
        first: pageSize,
        after: createAfterCursor(currentPage, pageSize),
        ...addIf(orderBy, 'orderBy'),
        ...addIf(levels, 'levels', getKeysWithTrueValues),
        ...addIf(search, 'search'),
        ...addIf(startDateIso, 'createdAt_Gte'),
        ...addIf(endDateIso, 'createdAt_Lte'),
        ...addIf(mpPk, 'mpPk', Number),
    } as NotificationsQueryVariables;
}

export function useNotificationQueryGenerator(
    orderAndPaginationControl: UseOrderAndPaginationControlReturn,
    filtrationControl: FiltrationControl
) {
    const [notifications, setNotifications] = useState<NotificationEdge[]>([]);
    const { showBoundary } = useErrorBoundary();
    const { currentPage, pageSize, orderBy, setCurrentPage, setTotalCount } =
        orderAndPaginationControl;
    const {
        filters: { levels, search, startDate, endDate, mpPk },
    } = filtrationControl;
    const committedFilterSignature = useMemo(
        () => getCommittedFilterSignature({ levels, search, startDate, endDate, mpPk }),
        [endDate, levels, mpPk, search, startDate]
    );
    const effectiveCurrentPage = useResetPageOnFilterChange(
        committedFilterSignature,
        currentPage,
        setCurrentPage
    );
    const queryVariables = useMemo(
        () =>
            createQueryVariables({
                currentPage: effectiveCurrentPage,
                pageSize,
                orderBy,
                levels,
                search,
                startDate,
                endDate,
                mpPk,
            }),
        [effectiveCurrentPage, endDate, levels, mpPk, orderBy, pageSize, search, startDate]
    );

    const { data, loading, networkStatus, error } = useQuery<
        NotificationsQuery,
        NotificationsQueryVariables
    >(NOTIFICATIONS, {
        variables: queryVariables,
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        if (error) {
            showBoundary(error);
        }
    }, [error, showBoundary]);

    useEffect(() => {
        if (data?.notifications) {
            setNotifications(data.notifications.edges as NotificationEdge[]);
            setTotalCount(data.notifications.totalCount || 0);
        }
    }, [data, setTotalCount]);

    return {
        notifications,
        loading: loading && networkStatus === NetworkStatus.loading,
        updating: loading && networkStatus === NetworkStatus.setVariables,
    };
}

export function useHover(): [boolean, { onMouseEnter: () => void; onMouseLeave: () => void }] {
    const [isHovering, setIsHovering] = useState<boolean>(false);

    function handleMouseEnter(): void {
        return setIsHovering(true);
    }

    function handleMouseLeave(): void {
        return setIsHovering(false);
    }

    const hoverProps = {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
    };

    return [isHovering, hoverProps];
}
